import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, Info, Loader2, X } from 'lucide-react';
import { useSidebar } from '../context/SidebarContext';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import LoadingAnimation from '../components/LoadingAnimation';
import VoiceSelector from '../components/VoiceSelector';


const videoStyleLabels: Record<string, string> = {
  vlog: 'Vlog',
  visitando_negocio: 'Visitando tu negocio',
  unboxing: 'Unboxing',
  automovil: 'Automóvil',
  podcast: 'Podcast',
  exterior: 'Exterior',
  entrevista: 'Entrevista',
  comparativa: 'Comparativa',
};

type CaptionStyle = { id: string; category: string; gifPath: string };
const CAPTION_CATEGORIES = ['Simple', 'Clasico', 'Moderno', 'Visible', 'Impacto', 'Llamativo'] as const;
const CAPTION_STYLES: CaptionStyle[] = [
  // Simple
  { id: '1bb3b68b-6a93-453a-afd7-a774b62cdab8', category: 'Simple', gifPath: '/images/captions styles/Simple/1bb3b68b-6a93-453a-afd7-a774b62cdab8.gif' },
  { id: '07ffd4b8-4e1a-4ee3-8921-d58802953bcd', category: 'Simple', gifPath: '/images/captions styles/Simple/07ffd4b8-4e1a-4ee3-8921-d58802953bcd.gif' },
  { id: '14bcd077-3f98-465b-b788-1b628951c340', category: 'Simple', gifPath: '/images/captions styles/Simple/14bcd077-3f98-465b-b788-1b628951c340.gif' },
  { id: 'a5619dcb-199d-4c6d-af05-6e5d5daef601', category: 'Simple', gifPath: '/images/captions styles/Simple/a5619dcb-199d-4c6d-af05-6e5d5daef601.gif' },
  { id: 'cc4b8197-2d49-4cc7-9f77-d9fbd8ef96ab', category: 'Simple', gifPath: '/images/captions styles/Simple/cc4b8197-2d49-4cc7-9f77-d9fbd8ef96ab.gif' },
  { id: 'cfa6a20f-cacc-4fb6-b1d0-464a81fed6cf', category: 'Simple', gifPath: '/images/captions styles/Simple/cfa6a20f-cacc-4fb6-b1d0-464a81fed6cf.gif' },
  { id: 'eb5de878-2997-41fe-858a-726e9e3712df', category: 'Simple', gifPath: '/images/captions styles/Simple/eb5de878-2997-41fe-858a-726e9e3712df.gif' },
  // Clasico
  { id: '7b946549-ae16-4085-9dd3-c20c82504daa', category: 'Clasico', gifPath: '/images/captions styles/Clasico/7b946549-ae16-4085-9dd3-c20c82504daa.gif' },
  { id: '9a2b0ed5-231b-4052-9211-5af9dc2de65e', category: 'Clasico', gifPath: '/images/captions styles/Clasico/9a2b0ed5-231b-4052-9211-5af9dc2de65e.gif' },
  // Moderno
  { id: '1c0c9b65-47c4-41bf-a187-25a8305fd0dd', category: 'Moderno', gifPath: '/images/captions styles/Moderno/1c0c9b65-47c4-41bf-a187-25a8305fd0dd.gif' },
  { id: '21327a45-df89-46bc-8d56-34b8d29d3a0e', category: 'Moderno', gifPath: '/images/captions styles/Moderno/21327a45-df89-46bc-8d56-34b8d29d3a0e.gif' },
  { id: '982ad276-a76f-4d80-a4e2-b8fae0038464', category: 'Moderno', gifPath: '/images/captions styles/Moderno/982ad276-a76f-4d80-a4e2-b8fae0038464.gif' },
  { id: 'c88bff11-7f03-4066-94cd-88f71f9ecc68', category: 'Moderno', gifPath: '/images/captions styles/Moderno/c88bff11-7f03-4066-94cd-88f71f9ecc68.gif' },
  // Visible
  { id: '6255949c-4a52-4255-8a67-39ebccfaa3ef', category: 'Visible', gifPath: '/images/captions styles/Visible/6255949c-4a52-4255-8a67-39ebccfaa3ef.gif' },
  { id: 'ca050348-e2d0-49a7-9c75-7a5e8335c67d', category: 'Visible', gifPath: '/images/captions styles/Visible/ca050348-e2d0-49a7-9c75-7a5e8335c67d.gif' },
  { id: 'd2018215-2125-41c1-940e-f13b411fff5c', category: 'Visible', gifPath: '/images/captions styles/Visible/d2018215-2125-41c1-940e-f13b411fff5c.gif' },
  { id: 'decf5309-2094-4257-a646-cabe1f1ba89a', category: 'Visible', gifPath: '/images/captions styles/Visible/decf5309-2094-4257-a646-cabe1f1ba89a.gif' },
  // Impacto
  { id: '55267be2-9eec-4d06-aff8-edcb401b112e', category: 'Impacto', gifPath: '/images/captions styles/Impacto/55267be2-9eec-4d06-aff8-edcb401b112e.gif' },
  { id: 'd46bb0da-cce0-4507-909d-fa8904fb8ed7', category: 'Impacto', gifPath: '/images/captions styles/Impacto/d46bb0da-cce0-4507-909d-fa8904fb8ed7.gif' },
  { id: 'e659ee0c-53bb-497e-869c-90f8ec0a921f', category: 'Impacto', gifPath: '/images/captions styles/Impacto/e659ee0c-53bb-497e-869c-90f8ec0a921f.gif' },
  { id: 'e7e758de-4eb4-460f-aeca-b2801ac7f8cc', category: 'Impacto', gifPath: '/images/captions styles/Impacto/e7e758de-4eb4-460f-aeca-b2801ac7f8cc.gif' },
  // Llamativo
  { id: '46d20d67-255c-4c6a-b971-31fddcfea7f0', category: 'Llamativo', gifPath: '/images/captions styles/Llamativo/46d20d67-255c-4c6a-b971-31fddcfea7f0.gif' },
  { id: '50cdfac1-0a7a-48dd-af14-4d24971e213a', category: 'Llamativo', gifPath: '/images/captions styles/Llamativo/50cdfac1-0a7a-48dd-af14-4d24971e213a.gif' },
  { id: '5de632e7-0b02-4d15-8137-e004871e861b', category: 'Llamativo', gifPath: '/images/captions styles/Llamativo/5de632e7-0b02-4d15-8137-e004871e861b.gif' },
  { id: 'a104df87-5b1a-4490-8cca-62e504a84615', category: 'Llamativo', gifPath: '/images/captions styles/Llamativo/a104df87-5b1a-4490-8cca-62e504a84615.gif' },
  { id: 'a51c5222-47a7-4c37-b052-7b9853d66bf6', category: 'Llamativo', gifPath: '/images/captions styles/Llamativo/a51c5222-47a7-4c37-b052-7b9853d66bf6.gif' },
  { id: 'a6760d82-72c1-4190-bfdb-7d9c908732f1', category: 'Llamativo', gifPath: '/images/captions styles/Llamativo/a6760d82-72c1-4190-bfdb-7d9c908732f1.gif' },
  { id: 'dfe027d9-bd9d-4e55-a94f-d57ed368a060', category: 'Llamativo', gifPath: '/images/captions styles/Llamativo/dfe027d9-bd9d-4e55-a94f-d57ed368a060.gif' },
];
const DEFAULT_CAPTION_ID = '1bb3b68b-6a93-453a-afd7-a774b62cdab8';

const ConfigurarVideoInfluencerVideo: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map(s => s.trim()).filter(Boolean).filter(s => s !== '0'),
    [idsParam],
  );
  const styleId = params.get('style') || 'vlog';
  const styleLabel = videoStyleLabels[styleId] || styleId;
  const imageIdFromParams = params.get('imageId') || '';
  const companyId = params.get('companyId') || '';

  // Allow overriding imageId and restoring dialogue when returning from editor
  const locationState = location.state as { editedImageId?: string; savedDialogue?: string; savedVoiceId?: string; savedDuration?: number } | null;
  const imageId = locationState?.editedImageId || imageIdFromParams;

  const { sidebarWidth, setMobileTitleOverride, setMobileBackOverride } = useSidebar();

  // Generated image URL
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);

  // Voice & dialogue
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(locationState?.savedVoiceId || null);
  const [selectedVoiceName, setSelectedVoiceName] = useState<string>('');
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [dialogue, setDialogue] = useState<string>(locationState?.savedDialogue || '');
  const [loadingDialogue, setLoadingDialogue] = useState<boolean>(!locationState?.savedDialogue);
  const [duration, setDuration] = useState<number>(locationState?.savedDuration || 8);
  const [mobileImageExpanded, setMobileImageExpanded] = useState(false);
  const [subtitles, setSubtitles] = useState(false);
  const [selectedCaptionId, setSelectedCaptionId] = useState<string>(DEFAULT_CAPTION_ID);
  const [showCaptionsMenu, setShowCaptionsMenu] = useState(false);
  const [captionsMenuAnimated, setCaptionsMenuAnimated] = useState(false);
  const [captionsMenuDragY, setCaptionsMenuDragY] = useState(0);
  const captionsDragStartY = useRef<number | null>(null);
  const [captionCategory, setCaptionCategory] = useState<string>('Simple');

  // Generate video state
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [completeProgressNow, setCompleteProgressNow] = useState(false);
  const willNavigateRef = useRef(false);
  const pendingNavRef = useRef<{ videoId: string; companyId: string } | null>(null);

  // Insufficient images modal state
  const [showInsufficientImagesModal, setShowInsufficientImagesModal] = useState(false);
  const [requiredImagesCount, setRequiredImagesCount] = useState(24);
  const [remainingImagesCount, setRemainingImagesCount] = useState(0);
  const [nextRenewalDate, setNextRenewalDate] = useState<Date | null>(null);

  // Fetch voice name when selectedVoiceId is restored from state
  useEffect(() => {
    if (!selectedVoiceId) return;
    const fetchVoiceName = async () => {
      const { data } = await supabase.from('voces').select('nombre').eq('id', selectedVoiceId).single();
      if (data?.nombre) {
        const short = data.nombre.includes(' - ') ? data.nombre.split(' - ')[0] : data.nombre;
        setSelectedVoiceName(short);
      }
    };
    if (!selectedVoiceName) fetchVoiceName();
  }, [selectedVoiceId]);

  // Fetch the generated image signed URL — polls until file_name is available
  useEffect(() => {
    if (!imageId || !companyId) { setImageLoading(false); return; }
    let cancelled = false;

    const fetchImage = async () => {
      const maxAttempts = 10;
      const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (cancelled) return;
        try {
          const { data: imgRow, error: imgError } = await supabase
            .from('imagenes_generadas')
            .select('file_name')
            .eq('id', imageId)
            .eq('id_company', companyId)
            .single();

          if (imgError || !imgRow?.file_name) {
            console.log(`[ConfigurarVideoInfluencerVideo] Attempt ${attempt}: no file_name yet for ${imageId}`);
            if (attempt < maxAttempts) { await delay(1500); continue; }
            break;
          }

          const filePath = `${companyId}/full/${imgRow.file_name}`;
          const { data: signed } = await supabase.storage
            .from('generadas')
            .createSignedUrl(filePath, 3600);

          if (signed?.signedUrl && !cancelled) {
            setImageUrl(signed.signedUrl);
          }
          break;
        } catch (err) {
          console.error(`[ConfigurarVideoInfluencerVideo] Attempt ${attempt} error:`, err);
          if (attempt < maxAttempts) { await delay(1500); continue; }
        }
      }
      if (!cancelled) setImageLoading(false);
    };

    fetchImage();
    return () => { cancelled = true; };
  }, [imageId, companyId]);

  // Auto-generate dialogue on mount (guarded to prevent double-fire in Strict Mode)
  const dialogueFetchedRef = useRef(!!locationState?.savedDialogue);
  useEffect(() => {
    if (dialogueFetchedRef.current) return;
    dialogueFetchedRef.current = true;

    const fetchDialogue = async () => {
      try {
        setLoadingDialogue(true);

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No se encontró el usuario');

        const variantId = ids[0] || '';
        if (!variantId) {
          setDialogue('');
          setLoadingDialogue(false);
          return;
        }

        const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/generar_dialogo', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': GENERATE_AUTH_TOKEN,
          },
          body: JSON.stringify({
            user_id: user.id,
            variante_id: variantId,
            tipo_de_video: styleId,
          }),
        });

        if (!response.ok) throw new Error('Error al generar el diálogo');

        const data = await response.json();
        setDialogue(data?.response || '');
      } catch (e) {
        console.error('Error fetching dialogue:', e);
        setDialogue('');
      } finally {
        setLoadingDialogue(false);
      }
    };

    fetchDialogue();
  }, [ids]);

  // Allowed discrete duration values
  const DURATION_OPTIONS = [4, 6, 8, 12, 14, 16, 20, 22, 24, 28, 30, 32];
  const durationIndex = DURATION_OPTIONS.indexOf(duration) !== -1 ? DURATION_OPTIONS.indexOf(duration) : 0;

  // Auto-set duration to recommended value based on dialogue length
  useEffect(() => {
    const trimmed = dialogue.trim();
    if (!trimmed) return;
    const wordCount = trimmed.split(/\s+/).length;
    const rawRecommended = Math.max(4, Math.ceil(wordCount / 3));
    // Snap to closest allowed value
    const snapped = DURATION_OPTIONS.reduce((prev, curr) =>
      Math.abs(curr - rawRecommended) < Math.abs(prev - rawRecommended) ? curr : prev
    );
    setDuration(snapped);
  }, [dialogue]);

  // Mobile header
  useEffect(() => {
    setMobileTitleOverride(' ');
    return () => setMobileTitleOverride(null);
  }, [setMobileTitleOverride]);

  useEffect(() => {
    setMobileBackOverride(() => handleBack);
    return () => setMobileBackOverride(null);
  }, [setMobileBackOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditImage = () => {
    if (!imageId || !companyId) return;
    // Build the return URL so EditarImagen can navigate back here
    const returnUrl = location.pathname + location.search;
    navigate(`/redes-sociales/editar-imagen?id=${imageId}&companyId=${companyId}`, {
      state: {
        returnToVideo: true,
        returnUrl,
        videoPageParams: { ids: idsParam, style: styleId, companyId },
        savedDialogue: dialogue,
        savedVoiceId: selectedVoiceId,
        savedDuration: duration,
      },
    });
  };

  const handleGenerateVideo = async () => {
    if (generating) return;
    setGenerating(true);
    setGenError(null);
    setShowLoadingScreen(true);
    setCompleteProgressNow(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      // Check image quota before generating video
      const requiredImages = duration * 2;
      if (companyId) {
        const { data: companyProfile } = await supabase
          .from('company_profiles')
          .select('plan, imagenes_restantes, max_fotos_mes, paid_plan_start_date, upgrade_plan_date')
          .eq('id_company', companyId)
          .single();

        const remaining = (companyProfile as any)?.imagenes_restantes as number | null | undefined;
        if (typeof remaining === 'number' && remaining < requiredImages) {
          setRequiredImagesCount(requiredImages);
          setRemainingImagesCount(remaining);
          const paidStart = (companyProfile as any)?.paid_plan_start_date as string | null;
          const upgradeDate = (companyProfile as any)?.upgrade_plan_date as string | null;
          if (paidStart) {
            const now = new Date();
            const ref = upgradeDate ? new Date(upgradeDate) : new Date(paidStart);
            const anchorDay = ref.getDate();
            const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
            const anchorFor = (y: number, m: number) => new Date(y, m, Math.min(anchorDay, daysInMonth(y, m)));
            let start = anchorFor(now.getFullYear(), now.getMonth());
            if (start.getTime() > now.getTime()) {
              const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
              start = anchorFor(prev.getFullYear(), prev.getMonth());
            }
            const next = new Date(start.getFullYear(), start.getMonth() + 1, 1);
            setNextRenewalDate(anchorFor(next.getFullYear(), next.getMonth()));
          } else {
            setNextRenewalDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1));
          }
          setShowInsufficientImagesModal(true);
          setGenerating(false);
          setShowLoadingScreen(false);
          return;
        }
      }

      const requestData: Record<string, string> = {
        user_id: user.id,
        tipo_de_video: styleId,
        dialogo: dialogue.trim(),
        imagen_id: imageId,
      };
      if (selectedVoiceId) {
        requestData.voz_id = selectedVoiceId;
      }
      requestData.duracion = String(duration);
      if (subtitles) {
        requestData.caption_id = selectedCaptionId;
      }

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/e2deb7a9-09f7-42ab-aebd-c7b1d4e6ab1a', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Error ${response.status}`);
      }

      const data = await response.json();
      if (data?.response === 'success' && data?.video_generado_id) {
        const videoId = String(data.video_generado_id);
        willNavigateRef.current = true;
        pendingNavRef.current = { videoId, companyId };
        setCompleteProgressNow(true);
      } else {
        throw new Error('No se recibió el video generado.');
      }
    } catch (e: any) {
      console.error('[ConfigurarVideoInfluencerVideo] handleGenerateVideo error', e);
      setGenError('Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
      setGenerating(false);
      setShowLoadingScreen(false);
    }
  };

  // Captions menu handlers
  const openCaptionsMenu = () => {
    setShowCaptionsMenu(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setCaptionsMenuAnimated(true)));
  };
  const closeCaptionsMenu = () => {
    setCaptionsMenuAnimated(false);
    setTimeout(() => setShowCaptionsMenu(false), 300);
  };
  const handleCaptionsTouchStart = (e: React.TouchEvent) => {
    captionsDragStartY.current = e.touches[0].clientY;
  };
  const handleCaptionsTouchMove = (e: React.TouchEvent) => {
    if (captionsDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - captionsDragStartY.current;
    if (deltaY > 0) setCaptionsMenuDragY(deltaY);
  };
  const handleCaptionsTouchEnd = () => {
    if (captionsMenuDragY > 100) closeCaptionsMenu();
    setCaptionsMenuDragY(0);
    captionsDragStartY.current = null;
  };
  const captionItemsForCategory = useMemo(
    () => CAPTION_STYLES.filter(c => c.category === captionCategory),
    [captionCategory],
  );
  const selectedCaptionCategory = useMemo(
    () => CAPTION_STYLES.find(c => c.id === selectedCaptionId)?.category || 'Simple',
    [selectedCaptionId],
  );

  // Image content
  const cardImageContent = imageLoading ? (
    <div className="w-full h-full flex items-center justify-center bg-neutral-100">
      <Loader2 className="w-8 h-8 text-[#1644EB] animate-spin" />
    </div>
  ) : imageUrl ? (
    <img
      src={imageUrl}
      alt={styleLabel}
      className="w-full rounded-lg"
    />
  ) : (
    <div className="w-full h-full flex items-center justify-center bg-neutral-100">
      <span className="material-symbols-rounded text-[40px] text-brand-black/20">image</span>
    </div>
  );

  // Options form — Voice + Dialogue
  const optionsForm = (
    <div>
      {/* Diálogo */}
      <div>
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Diálogo</p>

        {/* Voice row */}
        <div className="rounded-[13px] bg-white shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
          <button
            type="button"
            onClick={() => setShowVoiceSelector(true)}
            className="w-full px-4 py-[11px] flex items-center justify-between rounded-[13px]"
          >
            <span className="flex items-center gap-3">
              <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">record_voice_over</span>
              <span className="text-[15px] text-brand-black/90">Voz</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="text-[15px] text-[#3C3C43]/60">{selectedVoiceName || 'Seleccionar'}</span>
              <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
            </span>
          </button>
        </div>

        {/* Dialogue content — iOS card */}
        <div className="mt-3">
          {loadingDialogue ? (
            <div className="bg-white rounded-[13px] p-4 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.04)]">
              <div className="flex items-center gap-2 mb-3">
                <Loader2 className="w-5 h-5 text-[#1644EB] animate-spin" />
                <span className="text-[15px] text-[#3C3C43]/60">Generando diálogo...</span>
              </div>
              <div className="w-full h-1 bg-black/[0.06] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#1644EB] rounded-full"
                  style={{ animation: 'dialogueProgress 15s linear forwards' }}
                />
              </div>
              <style>{`
                @keyframes dialogueProgress {
                  from { width: 0%; }
                  to { width: 100%; }
                }
              `}</style>
            </div>
          ) : (
            <div className="bg-white rounded-[13px] p-4 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.04)]">
              <p className="text-[13px] text-[#3C3C43]/60 mb-2">Este es el texto que dirá el influencer en el video.</p>
              <textarea
                rows={6}
                value={dialogue}
                onChange={(e) => setDialogue(e.target.value)}
                placeholder="El diálogo aparecerá aquí..."
                className="w-full rounded-[10px] border-none bg-black/[0.04] p-3 text-[15px] text-brand-black/90 placeholder:text-brand-black/25 focus:outline-none focus:ring-0 resize-none"
              />
              {(() => {
                const trimmed = dialogue.trim();
                if (!trimmed) {
                  return (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-black/[0.06]">
                      <Info className="w-4 h-4 text-[#1644EB] shrink-0" />
                      <span className="text-[13px] text-[#3C3C43]/60">
                        Por favor ingresá el diálogo que dirá el influencer en el video.
                      </span>
                    </div>
                  );
                }
                const wordCount = trimmed.split(/\s+/).length;
                const recommendedSeconds = Math.max(6, Math.ceil(wordCount / 3));
                return (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-black/[0.06]">
                    <Info className="w-4 h-4 text-[#1644EB] shrink-0" />
                    <span className="text-[13px] text-[#3C3C43]/60">
                      Para este diálogo se recomienda una duración de video de {recommendedSeconds} segundos.
                    </span>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Duración */}
      <div className="mt-6">
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Duración</p>
        <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
          <div className="px-4 py-[11px]">
            <div className="flex items-center gap-3 mb-3">
              <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">timer</span>
              <span className="text-[15px] text-brand-black/90">Duración del video</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1 flex flex-col">
                <div className="relative h-[22px] flex items-center">
                  <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[6px] rounded-full bg-neutral-200" />
                  <div className="absolute top-1/2 -translate-y-1/2 left-0 h-[6px] rounded-full bg-[#1644EB]" style={{ width: `${(durationIndex / (DURATION_OPTIONS.length - 1)) * 100}%` }} />
                  <input
                    type="range"
                    min={0}
                    max={DURATION_OPTIONS.length - 1}
                    step={1}
                    value={durationIndex}
                    onChange={(e) => setDuration(DURATION_OPTIONS[Number(e.target.value)])}
                    className="absolute inset-0 w-full appearance-none bg-transparent cursor-pointer z-10 m-0 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[22px] [&::-webkit-slider-thumb]:h-[22px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0.5px_4px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.04)] [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-[22px]"
                  />
                </div>
                <div className="relative h-4 mt-1">
                  {DURATION_OPTIONS.map((v, i) => {
                    const pct = (i / (DURATION_OPTIONS.length - 1)) * 100;
                    const isKey = [4, 8, 16, 24, 32].includes(v);
                    const isActive = v <= duration;
                    return (
                      <span
                        key={v}
                        className={`absolute text-[11px] tabular-nums -translate-x-1/2 ${
                          isKey
                            ? (isActive ? 'text-[#1644EB]/60' : 'text-neutral-400')
                            : (isActive ? 'hidden sm:inline text-[#1644EB]/60' : 'hidden sm:inline text-neutral-400')
                        }`}
                        style={{ left: `${pct}%` }}
                      >{v}s</span>
                    );
                  })}
                </div>
              </div>
              <span className="text-[15px] font-medium text-brand-black/90 tabular-nums shrink-0 min-w-[58px] text-right">{duration} seg</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 px-4 mt-3">
        <Info className="w-3.5 h-3.5 text-[#3C3C43]/40 shrink-0" />
        <p className="text-[13px] text-[#3C3C43]/60">Se consumirán {duration * 2} imágenes.</p>
      </div>

      {/* Subtítulos toggle */}
      <div className="mt-6 mb-6 sm:mb-0">
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Opciones</p>
        <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
          <div className="px-4 py-[11px] flex items-center justify-between">
            <span className="flex items-center gap-3 flex-1 min-w-0">
              <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">subtitles</span>
              <span className="text-left min-w-0">
                <span className="text-[15px] text-brand-black/90">Agregar subtítulos</span>
                <span className="block text-[13px] text-[#3C3C43]/60 mt-0.5">Texto superpuesto en el video</span>
              </span>
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={subtitles}
              onClick={() => setSubtitles(!subtitles)}
              className={`relative w-[51px] h-[31px] rounded-full transition-colors duration-200 shrink-0 ml-3 ${subtitles ? 'bg-[#1644EB]' : 'bg-black/[0.09]'}`}
            >
              <div className={`absolute top-[2px] w-[27px] h-[27px] bg-white rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.15)] transition-transform duration-200 ${subtitles ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
            </button>
          </div>
          {subtitles && (
            <>
              <div className="ml-[46px] h-px bg-black/[0.08]" />
              <button
                type="button"
                onClick={openCaptionsMenu}
                className="w-full px-4 py-[11px] flex items-center justify-between"
              >
                <span className="flex items-center gap-3">
                  <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">tune</span>
                  <span className="text-[15px] text-brand-black/90">Configurar subtítulos</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="text-[15px] text-[#3C3C43]/60">{selectedCaptionCategory}</span>
                  <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
                </span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-[#EFF0F5]">
      {/* Desktop floating back button */}
      <div className="hidden sm:block fixed top-0 right-0 z-40" style={{ left: sidebarWidth }}>
        <div className="max-w-6xl mx-auto px-8 h-14 flex items-center">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Atrás</span>
          </button>
        </div>
      </div>

      {/* Desktop layout: image left, options right */}
      <div className="hidden sm:flex max-w-6xl mx-auto px-8 pt-14 gap-6">
        <div className="w-[400px] shrink-0 sticky top-[80px] self-start">
          <p className="text-[17px] font-semibold text-brand-black/90 mb-3">{styleLabel}</p>
          <div>
            {cardImageContent}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          {optionsForm}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={handleEditImage}
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-[10px] text-[15px] font-medium border border-neutral-200 bg-white text-brand-black/90 hover:bg-neutral-50 transition-all"
            >
              <span className="material-symbols-rounded text-[18px]">edit</span>
              Editar imagen
            </button>
            <button
              type="button"
              onClick={handleGenerateVideo}
              disabled={generating || loadingDialogue || !selectedVoiceId}
              className={`inline-flex items-center justify-center gap-2 h-11 px-8 rounded-[10px] text-[15px] font-medium transition-all ${
                (generating || loadingDialogue || !selectedVoiceId)
                  ? 'bg-[#1644EB]/40 text-white/60 cursor-not-allowed'
                  : 'bg-[#1644EB] hover:bg-[#1238C8] active:bg-[#0F2FA8] text-white'
              }`}
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generando...
                </>
              ) : 'Generar video'}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile layout: image top, options below */}
      <div className="sm:hidden">
        <div className="px-4 pt-4 flex justify-center">
          <div
            className="cursor-pointer"
            style={{
              width: mobileImageExpanded ? '80%' : 'calc(50% - 4px)',
              transition: 'width 0.35s cubic-bezier(0.25, 0.1, 0.25, 1)',
            }}
            onClick={() => setMobileImageExpanded(prev => !prev)}
          >
            {cardImageContent}
          </div>
        </div>
        <p className="text-[17px] font-semibold text-brand-black/90 text-center mt-2 px-4">{styleLabel}</p>

        {/* Editar imagen button — mobile */}
        <div className="px-4 mt-3">
          <button
            type="button"
            onClick={handleEditImage}
            className="w-full h-10 rounded-[10px] text-[15px] font-medium border border-neutral-200 bg-white text-brand-black/90 active:bg-neutral-50 transition-colors inline-flex items-center justify-center gap-2"
          >
            <span className="material-symbols-rounded text-[18px]">edit</span>
            Editar imagen
          </button>
        </div>

        {/* Options */}
        <div className="px-4 mt-3 mb-4">
          {optionsForm}
        </div>
      </div>

      {/* Mobile bottom bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white/72 backdrop-blur-xl border-t border-black/[0.06] px-4 py-3 z-30" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
        {loadingDialogue ? (
          <div className="flex items-center justify-center gap-2 text-[13px] text-[#3C3C43]/60 mb-2">
            <Loader2 className="w-3.5 h-3.5 text-[#1644EB] animate-spin" />
            <span>Generando diálogo...</span>
          </div>
        ) : null}
        <button
          type="button"
          onClick={handleGenerateVideo}
          disabled={generating || loadingDialogue || !selectedVoiceId}
          className={`w-full h-[50px] rounded-[13px] text-[17px] font-semibold transition-colors ${
            (generating || loadingDialogue || !selectedVoiceId)
              ? 'bg-black/[0.06] text-[#3C3C43]/30 cursor-not-allowed'
              : 'bg-[#1644EB] active:bg-[#1238C8] text-white shadow-[0_2px_8px_rgba(22,68,235,0.25)]'
          }`}
        >
          {generating ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Generando...
            </span>
          ) : 'Generar video'}
        </button>
      </div>

      {/* Loading Animation */}
      {showLoadingScreen && (
        <LoadingAnimation
          message="Generando video..."
          subMessage="Esto puede tardar unos segundos."
          durationMs={subtitles ? 150000 : 100000}
          completeNow={completeProgressNow}
          onCompleted={() => {
            setShowLoadingScreen(false);
            setGenerating(false);
            if (willNavigateRef.current && pendingNavRef.current) {
              willNavigateRef.current = false;
              const { videoId, companyId: cId } = pendingNavRef.current;
              pendingNavRef.current = null;
              navigate('/redes-sociales/imagenes-generadas', {
                state: { imageIds: [videoId], companyId: cId },
              });
            }
          }}
        />
      )}

      {/* Insufficient Images Modal */}
      {showInsufficientImagesModal && (
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-surface-card rounded-lg shadow-md w-full max-w-xl mx-4">
            <div className="p-5">
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-3">
                Imágenes insuficientes para generar video
              </h3>
              <p className="text-[15px] text-brand-black/90 mb-2">
                Para generar este video necesitás <strong>{requiredImagesCount} imágenes</strong>, pero {remainingImagesCount === 0 ? <>no te quedan imágenes restantes este mes</> : <>solo tenés <strong>{remainingImagesCount}</strong> restantes este mes</>}.
              </p>
              {nextRenewalDate && (
                <p className="text-[15px] text-neutral-500 mb-2">
                  Tu cupo de imágenes se renovará el {nextRenewalDate.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}.
                </p>
              )}
              <p className="text-[15px] text-brand-black/90 mb-6">
                Para generar videos ya mismo, podés mejorar tu plan.
              </p>
              <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => { setShowInsufficientImagesModal(false); navigate('/subscription'); }}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                >
                  Mejorar mi plan
                </button>
                <button
                  type="button"
                  onClick={() => setShowInsufficientImagesModal(false)}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error toast */}
      {genError && (
        <div className="fixed bottom-24 sm:bottom-8 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] p-4 z-50">
          <div className="flex items-start gap-3">
            <span className="material-symbols-rounded text-[22px] text-[#FF3B30] shrink-0">error</span>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-semibold text-brand-black/90">Error al generar</p>
              <p className="text-[13px] text-[#3C3C43]/60 mt-0.5">{genError}</p>
            </div>
            <button type="button" onClick={() => setGenError(null)} className="text-[#3C3C43]/30 hover:text-[#3C3C43]/60 transition-colors shrink-0 -mt-0.5">
              <span className="material-symbols-rounded text-[18px]">close</span>
            </button>
          </div>
        </div>
      )}

      {/* Desktop backdrop overlay when side panel is open */}
      {(showCaptionsMenu || showVoiceSelector) && (
        <div
          className="hidden sm:block fixed inset-0 bg-black/40 z-[45] cursor-pointer"
          onClick={() => { closeCaptionsMenu(); setShowVoiceSelector(false); }}
        />
      )}

      {/* Captions Style Menu */}
      {showCaptionsMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              captionsMenuAnimated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">Estilo de subtítulos</h3>
              <button type="button" onClick={closeCaptionsMenu} className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            {/* Category tabs */}
            <div className="px-5 pt-4 pb-2 flex flex-wrap gap-2">
              {CAPTION_CATEGORIES.map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCaptionCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
                    captionCategory === cat
                      ? 'bg-[#1644EB] text-white'
                      : 'bg-black/[0.05] text-brand-black/60 hover:bg-black/[0.08]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            {/* Grid */}
            <div className="flex-1 overflow-y-auto px-5 py-3">
              <div className="grid grid-cols-1 gap-3">
                {captionItemsForCategory.map(cap => {
                  const isSelected = selectedCaptionId === cap.id;
                  return (
                    <button
                      key={cap.id}
                      type="button"
                      onClick={() => setSelectedCaptionId(cap.id)}
                      className={`relative rounded-[10px] border-2 overflow-hidden transition-colors ${
                        isSelected ? 'border-[#1644EB]' : 'border-transparent hover:border-black/[0.08]'
                      }`}
                    >
                      <img
                        src={cap.gifPath}
                        alt={cap.category}
                        className="w-full aspect-[12/5] object-cover rounded-[8px]"
                        loading="lazy"
                      />
                      {isSelected && (
                        <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1644EB] text-white">
                          <Check className="w-3 h-3" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-5 py-3 flex justify-end">
              <button type="button" onClick={closeCaptionsMenu} className="text-[15px] font-semibold rounded-[10px] px-4 py-2 shadow-sm transition-colors text-white bg-[#1644EB] hover:bg-[#1238C8]">Aceptar</button>
            </div>
          </div>

          {/* Mobile Bottom Sheet — rendered via portal */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${captionsMenuAnimated ? 'opacity-100' : 'opacity-0'}`}
                onClick={closeCaptionsMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
                style={{
                  height: 'calc(100dvh - 1rem)',
                  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
                  transform: captionsMenuDragY > 0
                    ? `translateY(${captionsMenuDragY}px)`
                    : captionsMenuAnimated ? 'translateY(0)' : 'translateY(100%)',
                  transition: captionsMenuDragY === 0 ? 'transform 0.3s ease-out' : 'none',
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleCaptionsTouchStart}
                  onTouchMove={handleCaptionsTouchMove}
                  onTouchEnd={handleCaptionsTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button type="button" onClick={closeCaptionsMenu} className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Cancelar</button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">Subtítulos</h3>
                  <button type="button" onClick={closeCaptionsMenu} className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right">Aceptar</button>
                </div>
                {/* Category tabs */}
                <div className="px-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
                  {CAPTION_CATEGORIES.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setCaptionCategory(cat)}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
                        captionCategory === cat
                          ? 'bg-[#1644EB] text-white'
                          : 'bg-black/[0.05] text-brand-black/60 active:bg-black/[0.08]'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                {/* Grid */}
                <div className="flex-1 overflow-y-auto px-4 py-3">
                  <div className="grid grid-cols-1 gap-3">
                    {captionItemsForCategory.map(cap => {
                      const isSelected = selectedCaptionId === cap.id;
                      return (
                        <button
                          key={cap.id}
                          type="button"
                          onClick={() => setSelectedCaptionId(cap.id)}
                          className={`relative rounded-[10px] border-2 overflow-hidden transition-colors ${
                            isSelected ? 'border-[#1644EB]' : 'border-transparent active:border-black/[0.08]'
                          }`}
                        >
                          <img
                            src={cap.gifPath}
                            alt={cap.category}
                            className="w-full aspect-[12/5] object-cover rounded-[8px]"
                            loading="lazy"
                          />
                          {isSelected && (
                            <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1644EB] text-white">
                              <Check className="w-3 h-3" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Voice Selector */}
      <VoiceSelector
        open={showVoiceSelector}
        selectedVoiceId={selectedVoiceId}
        onSelect={(voiceId) => {
          setSelectedVoiceId(voiceId);
          // Update displayed name
          const fetchName = async () => {
            const { data } = await supabase.from('voces').select('nombre').eq('id', voiceId).single();
            if (data?.nombre) {
              const short = data.nombre.includes(' - ') ? data.nombre.split(' - ')[0] : data.nombre;
              setSelectedVoiceName(short);
            }
          };
          fetchName();
        }}
        onClose={() => setShowVoiceSelector(false)}
      />
    </div>
  );
};

export default ConfigurarVideoInfluencerVideo;
